import { Button, Empty, Table } from 'antd';
import moment from 'moment';
import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { useHistory } from 'react-router';
import CustomFilterDropdown from '../../../components/CustomFilterDropdown';
import { CompanyUserProfile } from '../../../models/company-user-profile-interface';
import { GetDateFormateStr } from '../../../utilites/dateTimeHelper';
import { TableAndExcelColumn } from '../../../utilites/exportToExcel';

type Filter = {
  text: string;
  value: number | string;
};

const roleFilters: Filter[] = [
  {
    value: 'Super User',
    text: 'Super User',
  },
  {
    value: 'Admin',
    text: 'Admin',
  },
  {
    value: 'Committee Admin',
    text: 'Committee Admin',
  },
  {
    value: 'Standard User',
    text: 'Standard User',
  },
];

const boolFilters: Filter[] = [
  {
    value: 'Yes',
    text: 'Yes',
  },
  {
    value: 'No',
    text: 'No',
  },
];

type Props = {
  users: CompanyUserProfile[];
  access: boolean;
  excludeDemo: boolean;
  setExcelColumns(columns: TableAndExcelColumn<CompanyUserProfile>[]): void;
  setFilteredData(dataType: string, newFilteredData: CompanyUserProfile[]): void;
};

const UserResultsTab: React.FC<Props> = ({
  users,
  access,
  excludeDemo,
  setExcelColumns,
  setFilteredData,
}) => {
  const history = useHistory();

  const getRoleDescription = (roleId: number, record: CompanyUserProfile) =>
    roleId === 3
      ? 'Admin'
      : roleId === 2
      ? 'Super User'
      : record.isCommitteeAdmin
      ? 'Committee Admin'
      : 'Standard User';

  const yesOrNo = (data: boolean) => !!data ? 'Yes' : 'No'

  const columns: TableAndExcelColumn<CompanyUserProfile>[] = useMemo(
    () => [
      {
        title: 'User ID',
        dataIndex: 'userId',
        key: 'userId',
        visible: true,
        render: (userId: number, record: CompanyUserProfile) => {
          return userId;
        },
        sorter: {
          compare: (a: CompanyUserProfile, b: CompanyUserProfile) =>
            a.userId === b.userId ? 0 : a.userId > b.userId ? -1 : 1,
        },
        excelHeader: 'User ID',
        excelRender: (record: CompanyUserProfile) => record.userId.toString(),
      },
      {
        title: 'First Name',
        dataIndex: 'firstName',
        key: 'firstName',
        visible: true,
        render: (firstName: string, record: CompanyUserProfile) => {
          return (
            <Button
              type='link'
              href={`/admin/user/${record.userId}`}
              onClick={(e) => {
                e.preventDefault();
                history.push(`/admin/user/${record.userId}`);
              }}
            >
              {firstName}
            </Button>
          );
        },
        sorter: {
          compare: (a: CompanyUserProfile, b: CompanyUserProfile) =>
            a.firstName.localeCompare(b.firstName),
        },
        excelHeader: 'First Name',
        excelRender: (record: CompanyUserProfile) => record.firstName,
      },
      {
        title: 'Last Name',
        dataIndex: 'lastName',
        key: 'lastName',
        visible: true,
        render: (lastName: string, record: CompanyUserProfile) => {
          return (
            <Button
              type='link'
              href={`/admin/user/${record.userId}`}
              onClick={(e) => {
                e.preventDefault();
                history.push(`/admin/user/${record.userId}`);
              }}
            >
              {lastName}
            </Button>
          );
        },
        sorter: {
          compare: (a: CompanyUserProfile, b: CompanyUserProfile) =>
            a.lastName.localeCompare(b.lastName),
        },
        excelHeader: 'Last Name',
        excelRender: (record: CompanyUserProfile) => record.lastName,
      },
      {
        title: 'Email Address',
        dataIndex: 'emailAddress',
        key: 'emailAddress',
        visible: true,
        render: (emailAddress: string, record: CompanyUserProfile) => {
          return (
            <Button
              type='link'
              href={`/admin/user/${record.userId}`}
              onClick={(e) => {
                e.preventDefault();
                history.push(`/admin/user/${record.userId}`);
              }}
            >
              {emailAddress}
            </Button>
          );
        },
        sorter: {
          compare: (a: CompanyUserProfile, b: CompanyUserProfile) =>
            a.emailAddress.localeCompare(b.emailAddress),
        },
        excelHeader: 'Email Address',
        excelRender: (record: CompanyUserProfile) => record.emailAddress,
      },
      {
        title: 'Access Until',
        dataIndex: 'csAccessExpiration',
        key: 'csAccessExpiration',
        visible: access,
        render: (csAccessExpiration: string) => {
          return csAccessExpiration !== null
            ? moment(csAccessExpiration).format(GetDateFormateStr(1))
            : null;
        },
        sorter: {
          compare: (a: CompanyUserProfile, b: CompanyUserProfile) => {
            let d1 = a.csAccessExpiration ?? new Date('01/01/1000');
            let d2 = b.csAccessExpiration ?? new Date('01/01/1000');
            return moment(d1).diff(moment(d2));
          },
        },
        excelHeader: 'Access Until',
        excelRender: (record: CompanyUserProfile) =>
          record.csAccessExpiration !== null
            ? moment(record.csAccessExpiration).format(GetDateFormateStr(1))
            : '',
      },
      {
        title: 'Company',
        dataIndex: 'companyName',
        key: 'companyName',
        visible: true,
        render: (companyName: string, record: CompanyUserProfile) => {
          return (
            <Button
              type='link'
              href={`/admin/company/${record.companyId}`}
              onClick={(e) => {
                e.preventDefault();
                history.push(`/admin/company/${record.companyId}`);
              }}
            >
              {companyName}
            </Button>
          );
        },
        sorter: {
          compare: (a: CompanyUserProfile, b: CompanyUserProfile) =>
            a.companyName.localeCompare(b.companyName),
        },
        excelHeader: 'Company',
        excelRender: (record: CompanyUserProfile) => record.companyName,
      },
      {
        title: 'Role',
        dataIndex: 'roleId',
        key: 'roleId',
        visible: true,
        render: (roleId: number, record: CompanyUserProfile) => 
          getRoleDescription(roleId, record),
        filters: roleFilters,
        onFilter: (value: string | any, record: CompanyUserProfile) => {
          let role1 = getRoleDescription(record.roleId, record);
          return role1 === value;
        },
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
          filters,
        }) => (
          <CustomFilterDropdown
            filters={filters}
            setSelectedKeys={setSelectedKeys}
            selectedKeys={selectedKeys}
            confirm={confirm}
            clearFilters={clearFilters}
            dataIndex='roleFilter'
          />
        ),
        sorter: {
          compare: (a: CompanyUserProfile, b: CompanyUserProfile) => {
            let role1 = getRoleDescription(a.roleId, a);
            let role2 = getRoleDescription(b.roleId, b);
            return role1.localeCompare(role2);
          },
        },
        excelHeader: 'Role',
        excelRender: (record: CompanyUserProfile) => 
          getRoleDescription(record.roleId, record)
      },
      {
        title: 'Notified',
        dataIndex: 'notificationDate',
        key: 'notificationDate',
        visible: true,
        render: (notificationDate: string, record: CompanyUserProfile) => 
          yesOrNo(!!notificationDate),
        filters: boolFilters,
        onFilter: (value: string | any, record: CompanyUserProfile) => {
          let notified = yesOrNo(!!record.notificationDate);
          return notified === value;
        },
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
          filters,
        }) => (
          <CustomFilterDropdown
            filters={filters}
            setSelectedKeys={setSelectedKeys}
            selectedKeys={selectedKeys}
            confirm={confirm}
            clearFilters={clearFilters}
            dataIndex='notifiedFilter'
          />
        ),
        sorter: {
          compare: (a: CompanyUserProfile, b: CompanyUserProfile) =>
            !!a.notificationDate && !!b.notificationDate
              ? 0
              : !!a.notificationDate
              ? -1
              : 1,
        },
        excelHeader: 'Notified',
        excelRender: (record: CompanyUserProfile) => 
          yesOrNo(!!record.notificationDate),
      },
      {
        title: 'Active',
        dataIndex: 'active',
        key: 'active',
        visible: true,
        render: (active: boolean, record: CompanyUserProfile) =>
          yesOrNo(active),
        filters: boolFilters,
        onFilter: (value: string | any, record: CompanyUserProfile) => {
          let active = yesOrNo(record.active);
          return active === value;
        },
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
          filters,
        }) => (
          <CustomFilterDropdown
            filters={filters}
            setSelectedKeys={setSelectedKeys}
            selectedKeys={selectedKeys}
            confirm={confirm}
            clearFilters={clearFilters}
            dataIndex='activeFilter'
          />
        ),
        sorter: {
          compare: (a: CompanyUserProfile, b: CompanyUserProfile) =>
            a.active === b.active ? 0 : a.active ? -1 : 1,
        },
        excelHeader: 'Active',
        excelRender: (record: CompanyUserProfile) =>
          yesOrNo(!!record.active),
      },
    ],
    [access, history]
  );

  useEffect(() => {
    setExcelColumns(columns);
  }, [columns, setExcelColumns]);

  return (
    <Table
      columns={columns.filter((x) => x.visible)}
      dataSource={users}
      size='middle'
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={`No users found`}
          />
        ),
      }}
      rowKey={(val) => `${val.userId}-${val.companyId}`}
      scroll={{ x: 715 }}
      pagination={{ defaultPageSize: 20 }}
      onChange={(pagigation, filters, sorter, extra) =>
        setFilteredData('users', extra.currentDataSource)
      }
    />
  );
};

export default UserResultsTab;
